import { useProfileRail } from './profileRail';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/** One fact in the rail — a label over its value, with a glyph naming the kind. */
export interface ProfileFact {
  key: string;
  /** Written in natural case; the component uppercases it. */
  label: string;
  icon?: LucideIcon;
  /**
   * The value. `null` and `undefined` both render the em dash, so a caller passes what the
   * record holds without testing it first.
   */
  value: ReactNode;
  /**
   * Renders the value as a machine value: `font-mono tabular-nums`. **Required on every date
   * and every code**, the same flag `<PreviewRow>` carries and for the same reason — a phone
   * number came off a machine, a name did not.
   */
  mono?: boolean;
}

/**
 * A stack of record facts for the sidebar of a profile — a glyph and a small-caps label over
 * the value, one per row, ruled between.
 *
 * **It LEADS the rail, above the panel picker** (product call, 27 Oct 2026). What the record
 * IS comes before where to read more about it, and a reader arriving at a profile answers
 * "who is this" before choosing a panel.
 *
 * **It is NOT `<PreviewTable>`.** That one is the label-LEFT/value-RIGHT field table for a
 * `<ProfileCard>`, and its proportional label column is unusable at the rail's 288px: a 38%
 * key column leaves 170px for an address. This stacks instead, which is what a narrow column
 * can carry — and it is the shape the reference system's own personnel rail uses, captured
 * from a screenshot of it (27 Oct 2026).
 *
 * **The rows line up with `<ProfileNav>`'s.** Its entries sit at `p-2` + `px-3`, so a glyph
 * starts 20px from the rail's edge; `px-5` here puts this glyph on that same line. Measured
 * rather than eyeballed — the two blocks are stacked in one column and a 4px step between
 * their icon gutters is the kind of thing only a ruler finds.
 */
export function ProfileFacts({ facts, id }: { facts: ProfileFact[]; id?: string }) {
  const { collapsed } = useProfileRail();

  /*
    A minimized rail hides the facts outright rather than shrinking them. There is no
    icon-width form of a label over a value — a glyph with no value beside it states nothing —
    and these are the one block in the rail that is repeated on the header strip above, which
    is what makes hiding them affordable here and would not be on the panel picker.
  */
  if (collapsed || facts.length === 0) {
    return null;
  }

  return (
    /*
      A real seam rather than a flush join: this block and the nav BELOW it are two regions of
      one rail, and stacked flush they read as one control that has come apart. The bottom rule
      is that seam; the nav's own `p-2` supplies the space under it, so nothing here adds a
      margin that would double against it.
    */
    <dl id={id} className="divide-y divide-slate-100 border-b border-slate-200">
      {facts.map((fact) => (
        <div key={fact.key} className="px-5 py-3">
          <dt className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {fact.icon ? <fact.icon className="h-3 w-3 shrink-0" aria-hidden="true" /> : null}
            {fact.label}
          </dt>

          {/*
            `break-words`, because the rail is 288px and an email or a long unit name has
            nowhere else to go. The value is the body tone — a rail is scanned, and the darker
            steps are the selection's and a heading's.
          */}
          <dd
            className={`mt-1 break-words text-sm text-slate-600 ${
              fact.mono ? 'font-mono tabular-nums' : ''
            }`}
          >
            {fact.value == null ? <span className="text-slate-400">—</span> : fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
